import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Logo, Copyright } from "../components/brand/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CtaButton } from "../components/common";

const HERO = "https://images.pexels.com/photos/7424584/pexels-photo-7424584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1100";

export const AuthShell = ({ children }) => (
  <div className="grid min-h-screen bg-white lg:grid-cols-2">
    <div className="flex flex-col px-6 py-10 sm:px-12 lg:px-20">
      <div className="flex flex-1 flex-col justify-center">
        <div className="mx-auto w-full max-w-[400px] lp-fade">{children}</div>
      </div>
      <Copyright className="mt-10" />
    </div>
    <div className="relative hidden overflow-hidden lg:block">
      <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#351080]/55" />
      <div className="absolute inset-x-0 bottom-0 p-14 text-white">
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Solusi Kredit Pensiun</p>
        <h2 className="font-display text-4xl font-bold leading-tight">Amanah • Terpercaya •<br />Dekat dengan Anda</h2>
        <p className="mt-4 max-w-md text-sm text-white/80">Portal internal untuk pengelolaan leads, simulasi Calc Platinum, dan pendampingan para pensiunan.</p>
      </div>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(errMsg(err, "Login gagal. Periksa koneksi Anda."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <Logo variant="vertical" size="sm" className="mb-8 hidden sm:block" data-testid="login-logo-vertical" />
      <Logo variant="horizontal" size="md" className="mb-8 sm:hidden" data-testid="login-logo-horizontal" />
      <p className="lp-eyebrow">Portal Internal</p>
      <h1 className="mt-2 font-display text-3xl font-bold text-[#17223B]">Masuk ke akun Anda</h1>
      <p className="mt-2 text-sm text-slate-500">Akun dibuat dan diundang oleh administrator yang berwenang.</p>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" data-testid="login-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Kata Sandi</Label>
            <Link to="/lupa-sandi" className="text-xs font-semibold text-[#5720B8] hover:underline" data-testid="forgot-password-link">Lupa kata sandi?</Link>
          </div>
          <div className="relative">
            <Input id="password" type={show ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-11" data-testid="login-password-input" />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-500 hover:text-[#351080]" aria-label={show ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"} data-testid="toggle-password-visibility">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <CtaButton type="submit" disabled={busy} className="h-11 w-full text-[15px] font-semibold" data-testid="login-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
        </CtaButton>
        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5 text-[#078D67]" /> Koneksi aman • Akses berbasis peran</p>
      </form>
    </AuthShell>
  );
}
